import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import apiService from '../services/apiService';
import { BookOpen, Heart, MessageCircle, Share2, Settings, ArrowLeft, Volume2, StopCircle, RefreshCw } from 'lucide-react'; // Added RefreshCw
import SentenceExtractor from '../components/SentenceExtractor';
import KaraokeText from '../components/KaraokeText';
import AnalyzedResult from '../components/AnalyzedResult'; // NEW
import MeaningPopup from '../components/MeaningPopup'; // NEW
import { useSmartTTS } from '../hooks/useSmartTTS'; // NEW for FeedItem internal speak

/* --- Styled Components --- */

const FeedContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background-color: #000;
  padding-bottom: 80px;
  color: #e5e5e5;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const HeaderBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid #333;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
`;

const HeaderTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.5px;
`;

const SettingsButton = styled.button`
  position: absolute;
  right: 16px;
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  &:hover {
    background: #222;
  }
`;

const FeedList = styled.div`
  max-width: 680px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
`;

const PostCard = styled.article`
  padding: 16px;
  border-bottom: 1px solid #222;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #000;
  transition: background 0.2s;

  &:active {
    background: #0a0a0a;
  }
`;

const PostHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1A2980, #26D0CE);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  color: white;
`;

const AuthorName = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: #fff;
  display: flex;
  flex-direction: column;
`;

const TopicBadge = styled.span`
  font-size: 12px;
  color: #888;
  font-weight: 400;
`;

const TimeStamp = styled.div`
  font-size: 13px;
  color: #666;
`;

const PostContent = styled.div`
  font-size: 16px;
  line-height: 1.6;
  color: #e5e5e5;
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

const KaraokeWrapper = styled.div`
  position: relative;
`;

const TranslationSection = styled.div`
  margin-top: 8px;
  padding: 12px;
  background: #111;
  border-radius: 8px;
  font-size: 14px;
  color: #aaa;
  line-height: 1.5;
  cursor: pointer;
  border: 1px solid #222;

  &:hover {
    background: #1a1a1a;
  }

  ${props => props.$hidden && `
    filter: blur(5px);
    opacity: 0.6;
    user-select: none;
  `}
`;

const PostImage = styled.img`
  width: 100%;
  border-radius: 12px;
  margin-top: 4px; 
  margin-bottom: 12px;
  border: 1px solid #222;
  max-height: 400px;
  object-fit: cover;
`;

const PostFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  padding-top: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.$active ? '#ff4b4b' : '#888'};
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 8px;
  border-radius: 20px;
  transition: all 0.2s;

  &:hover {
    background: rgba(255,255,255,0.05);
    color: ${props => props.$active ? '#ff4b4b' : '#ccc'};
  }
  
  svg {
    stroke-width: 2px;
  }
`;

/* --- New Action Bar Components --- */
const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  margin-top: 4px;
`;

const highlightPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(229, 9, 20, 0.7); }
  70% { box-shadow: 0 0 0 15px rgba(229, 9, 20, 0); }
  100% { box-shadow: 0 0 0 0 rgba(229, 9, 20, 0); }
`;

const HeaderActionButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 6px 12px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  ${props => props.$active && `
    background: rgba(229, 9, 20, 0.2);
    border-color: #e50914;
    color: #e50914;
  `}

  ${props => props.$highlight && css`
    animation: ${highlightPulse} 2s infinite;
    border-color: #e50914;
    color: #fff;
    background: rgba(229, 9, 20, 0.8);
  `}
`;

/* --- FeedItem Component --- */
export const FeedItem = React.memo(({ item, revealedIds, toggleReveal, handleLike, handleShare, setAnalysisText, setAnalysisFeedId, setShowAnalysis, setDeepDiveItem, getSourceDisplay, getTimeDisplay, handleSmartWordClick, onMeaningClick, preLoadedData, isGuest, onRequestLogin, isFirst, isGuestUser }) => {
  const karaokeRef = useRef();
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const isRevealed = revealedIds.has(item.id);

  // Inline Analysis State - Initialize with Pre-loaded Data!
  const [analysisData, setAnalysisData] = useState(preLoadedData || null);
  const [analysisDocId, setAnalysisDocId] = useState(null); // DocId might need separate fetch if we really need it for updating status, but for display it's fine.
  // Actually, without docId we can't toggle update status seamlessly.
  // But wait, batch check returns ID too? Yes, result.id.

  // Note: We might need to pass docId too if we want perfect functionality.
  // For now, let's assume 'preLoadedData' is the DATA ARRAY for now to match prop name.
  // If we need to update status (toggle complete), we need docId.
  // We can fetch docId lazily when user tries to interact, OR improve batch response to return ID + Data.
  // The current batch response returns: { hasHistory: true, id: doc.id, data: ... }
  // So we should pass the WHOLE object to FeedItem? 
  // Let's assume 'preLoadedData' is the DATA ARRAY for now to match prop name.

  // Update state if prop changes (e.g. late load)
  useEffect(() => {
    if (preLoadedData && !analysisData) {
      setAnalysisData(preLoadedData);
    }
  }, [preLoadedData]);

  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const { speak } = useSmartTTS();

  // Generate Audio URL from Firestore Blob or Server Endpoint
  useEffect(() => {
    if (item.hasAudio) {
      // Use Server Endpoint for streaming (Optimized)
      setAudioUrl(`${apiService.baseURL}/feed/${item.id}/audio`);
    } else if (item.audioData && item.audioData.type === 'Buffer') {
      try {
        // Fallback for legacy items (Inline Blob)
        const blob = new Blob([new Uint8Array(item.audioData.data)], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        return () => URL.revokeObjectURL(url);
      } catch (e) {
        console.error("Audio Blob Error", e);
      }
    }
  }, [item.hasAudio, item.audioData, item.id]);

  const togglePlay = (e) => {
    e.stopPropagation();
    if (karaokeRef.current) {
      karaokeRef.current.handlePlay();
    }
  };

  const handleAnalyzeClick = async () => {
    // If already showing, toggle off
    if (analysisData) {
      setAnalysisData(null);
      return;
    }

    const user = JSON.parse(localStorage.getItem('user'));

    // Guest Mode Support: Allow analysis without login
    if (!user?.id && !isGuest) {
      alert("문장 분석을 하려면 로그인이 필요합니다.");
      return;
    }

    setLoadingAnalysis(true);

    try {
      if (isGuest) {
        // 1. Try to fetch Pre-Calculated Analysis (Instant)
        const historyRes = await apiService.checkSentenceHistory(null, item.text, String(item.id));

        if (historyRes.found && historyRes.data) {
          setAnalysisData(historyRes.data);
        } else {
          // 2. Fallback to Real-time Analysis (Use CORRECT API: sentenceMatch)
          const res = await apiService.sentenceMatch([item.text]);
          if (res.success && res.data) {
            setAnalysisData(res.data);
          }
        }
      } else {
        // Force String ID
        const res = await apiService.checkSentenceHistory(user.id, item.text, String(item.id));

        if (res.found && res.data) {
          setAnalysisData(res.data);

          if (res.source === 'user_history') {
            setAnalysisDocId(res.id);
          } else if (res.source === 'pre_analysis') {
            // Background save to ensure we have a docId for updates
            apiService.saveUserSentence(user.id, res.data, item.text, { feedId: String(item.id) })
              .then(saveRes => {
                if (saveRes && saveRes.data && saveRes.data.id) {
                  setAnalysisDocId(saveRes.data.id);
                }
              })
              .catch(e => console.error("Failed to save pre-analysis", e));
          }
        } else {
          // Not found in cache/history, falling back to old modal behavior (Real-time AI)
          setAnalysisText(item.text);
          setAnalysisFeedId(item.id);
          setShowAnalysis(true);
        }
      }
    } catch (e) {
      console.error("Analysis Fetch Error", e);
      // Fallback on error - optionally alert user if critical, but modal is safer fallback
      setAnalysisText(item.text);
      setAnalysisFeedId(item.id);
      setShowAnalysis(true);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleToggleComplete = async (index) => {
    if (isGuest) {
      if (onRequestLogin) onRequestLogin();
      return;
    }

    if (!analysisData) return;

    const newData = [...analysisData];
    newData[index].completed = !newData[index].completed;
    setAnalysisData(newData);

    if (analysisDocId) {
      try {
        await apiService.updateUserSentence(analysisDocId, newData);
      } catch (e) {
        console.error("Failed to update status", e);
        // Revert
        newData[index].completed = !newData[index].completed;
        setAnalysisData([...newData]);
      }
    } else {
      console.warn("No analysisDocId available, cannot persist update yet.");
    }
  };

  // Find first uncompleted index for auto-scroll
  const firstUncompletedIndex = analysisData ? analysisData.findIndex(item => !item.completed) : -1;

  return (
    <PostCard>
      <PostHeader>
        <AuthorInfo>
          <Avatar>{getSourceDisplay(item)[0]}</Avatar>
          <AuthorName>
            <span>{getSourceDisplay(item)}</span>
            <TopicBadge>#{item.topic}</TopicBadge>
          </AuthorName>
        </AuthorInfo>
        <TimeStamp>{getTimeDisplay()}</TimeStamp>
      </PostHeader>

      {/* Action Bar (Listen, Analyze) - Placed Top as requested */}
      <ActionBar>
        <HeaderActionButton onClick={togglePlay} $active={isPlaying}>
          {isPlaying ? <StopCircle size={16} /> : <Volume2 size={16} />}
          {isPlaying ? "중지" : "듣기"}
        </HeaderActionButton>

        <HeaderActionButton 
          onClick={handleAnalyzeClick} 
          $active={!!analysisData || loadingAnalysis}
          $highlight={isGuestUser && isFirst && !analysisData && !loadingAnalysis}
        >
          {loadingAnalysis ? <RefreshCw size={16} className="spin" /> : <MessageCircle size={16} />}
          분석하기
        </HeaderActionButton>
      </ActionBar>

      {/* Image Attachment */}
      {item.imageUrl && (
        <PostImage src={item.imageUrl} alt="News Image" loading="lazy" />
      )}

      <PostContent>
        <KaraokeWrapper>
          <KaraokeText
            ref={karaokeRef}
            text={item.text}
            audioUrl={audioUrl} // Pass Pre-generated Audio
            onWordClick={async (word, index) => {
              // 1. Check existing analysis
              let currentAnalysis = analysisData;

              // 2. If not loaded, fetch from server
              if (!currentAnalysis) {
                try {
                  const user = JSON.parse(localStorage.getItem('user'));
                  const res = await apiService.checkSentenceHistory(user?.id, item.text, item.id);
                  if (res.found && res.data) {
                    currentAnalysis = res.data;
                    setAnalysisData(res.data); // Update state for future use
                    if (res.source === 'user_history') setAnalysisDocId(res.id);
                  }
                } catch (e) {
                  console.error("Failed to fetch analysis for word click", e);
                }
              }

              // 3. Search for the word in meaning units using INDEX (Position)
              if (currentAnalysis) {
                // Flatten meaning units: [ {source: "The cat", target:..}, {source: "is", target:..}, ... ]
                const allUnits = currentAnalysis.flatMap(sent => sent.meaningUnits || []);

                let wordCounter = 0;
                let foundUnit = null;

                // Iterate units and map their word ranges
                for (const unit of allUnits) {
                  // Count words in this unit. Match splitting logic of KaraokeText (/\s+/)
                  const unitWordCount = unit.source.trim().split(/\s+/).length;

                  // Check if the clicked index falls within this unit's range
                  if (index >= wordCounter && index < wordCounter + unitWordCount) {
                    foundUnit = unit;
                    break; // Found matching unit
                  }

                  wordCounter += unitWordCount;
                }

                // Fallback: If index mapping failed (e.g. text mismatch), try content match
                if (!foundUnit) {
                  foundUnit = allUnits.find(unit => unit && unit.source && unit.source.toLowerCase().includes(word.toLowerCase()));
                }

                if (foundUnit) {
                  onMeaningClick(foundUnit.source, foundUnit.target);
                  return;
                }
              }

              // Fallback: If no match or no analysis, just pass the word itself
              handleSmartWordClick(word, item.text, item.translation, item.id);
            }}
            showButton={false} // Hide internal button
            onStateChange={(state) => setIsPlaying(state)}
          />
        </KaraokeWrapper>
      </PostContent>

      {/* Translation or Inline Analysis */}
      {analysisData ? (
        <div style={{ marginTop: 12 }}>
          {analysisData.map((resItem, idx) => (
            <AnalyzedResult
              key={idx}
              item={resItem}
              idx={idx}
              speak={speak}
              onWordClick={onMeaningClick}
              onToggleComplete={handleToggleComplete}
              autoScroll={idx === firstUncompletedIndex}
            />
          ))}
        </div>
      ) : (
        <TranslationSection
          $hidden={!isRevealed}
          onClick={() => toggleReveal(item.id)}
        >
          {isRevealed ? item.translation : "탭하여 번역 보기"}
        </TranslationSection>
      )}

      {/* Footer Actions (Like, Share, Full Read) */}
      <PostFooter>
        <ActionButton
          $active={item.liked}
          onClick={() => isGuest ? onRequestLogin && onRequestLogin() : handleLike(item)}
        >
          <Heart size={20} fill={item.liked ? "#ff4b4b" : "none"} />
          {item.likes || 0}
        </ActionButton>

        {item.fullText && (
          <ActionButton onClick={() => isGuest ? onRequestLogin && onRequestLogin() : setDeepDiveItem(item)}>
            <BookOpen size={20} />
            전체 읽기
          </ActionButton>
        )}

        <ActionButton onClick={() => isGuest ? onRequestLogin && onRequestLogin() : handleShare(item)}>
          <Share2 size={20} />
        </ActionButton>
      </PostFooter>

    </PostCard>
  );
});

/* --- FeedPage Component --- */
function FeedPage() {
  const navigate = useNavigate();
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination State
  const [lastCreatedAt, setLastCreatedAt] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Interactive States
  const [revealedIds, setRevealedIds] = useState(new Set());
  const [deepDiveItem, setDeepDiveItem] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisText, setAnalysisText] = useState('');
  const [analysisFeedId, setAnalysisFeedId] = useState(null);

  // Popup States (For Inline Analysis)
  const [popup, setPopup] = useState(null);
  const [wordInfo, setWordInfo] = useState(null);
  const [loadingWord, setLoadingWord] = useState(false);

  const [analyzedStatus, setAnalyzedStatus] = useState({}); // { feedId: boolean }

  // Infinite Scroll
  const observer = useRef();
  const lastItemRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreFeed();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  useEffect(() => {
    // 1. Instant Cache Load
    const cached = localStorage.getItem('cached_feed_items');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setFeedItems(parsed);
        // Only valid if we have items
        if (parsed.length > 0) {
          setLastCreatedAt(parsed[parsed.length - 1].createdAt);
          setLoading(false); // Show content immediately
        }
      } catch (e) {
        console.error("Cache Parse Error", e);
      }
    }

    // 2. Fetch Fresh Data (Background Update)
    loadFeed(true);
  }, []);

  const getActiveTopics = () => {
    try {
      const saved = localStorage.getItem('feed_settings');
      if (saved) {
        const settings = JSON.parse(saved);
        return Object.keys(settings).filter(k => settings[k]);
      }
    } catch (e) { console.error(e); }
    return null;
  };

  const loadFeed = async (isInitial = false) => {
    if (!isInitial && !hasMore) return;

    try {
      // If we have cached items and it's initial load, don't show loading spinner overlay
      // Just let it update in background. If no cache, show spinner.
      if (feedItems.length === 0) setLoading(true);

      const requestType = isInitial ? 'init' : 'scroll';
      const topics = getActiveTopics();

      const params = {
        type: requestType,
        contentType: 'news',
        topics: topics,
        lastCreatedAt: isInitial ? null : lastCreatedAt
      };

      const response = await apiService.getFeed(params);

      const fetchedItems = response.data;

      // Batch Check History for new items
      const user = JSON.parse(localStorage.getItem('user'));
      if (user?.id && fetchedItems.length > 0) {
        try {
          const idsToCheck = fetchedItems.map(i => String(i.id));
          const batchRes = await apiService.checkSentenceHistoryBatch(user.id, idsToCheck);
          if (batchRes.success && batchRes.data) {
            // data is { feedId: { hasHistory: true, data: ... } }
            setAnalyzedStatus(prev => {
              const updates = {};
              Object.keys(batchRes.data).forEach(fid => {
                if (batchRes.data[fid].hasHistory) {
                  updates[fid] = batchRes.data[fid].data; // Store the DATA, not just true
                }
              });
              return { ...prev, ...updates };
            });
          }
        } catch (e) {
          console.error("Batch history check failed", e);
        }
      }

      // Handle Pagination
      if (fetchedItems.length < 5) { // Lower threshold for safety
        setHasMore(false);
      }

      if (fetchedItems.length > 0) {
        setLastCreatedAt(fetchedItems[fetchedItems.length - 1].createdAt);

        const newItems = fetchedItems.map(item => ({
          ...item,
          _renderId: `${item.id}`
        }));

        setFeedItems(prev => {
          // If Initial, completely replace (Fresh Start) - This might cause a jump but ensures consistency.
          // To merge smoothly: unique check
          if (isInitial) {
            // Save to Cache (Zero Latency for next time)
            localStorage.setItem('cached_feed_items', JSON.stringify(newItems));
            return newItems;
          }

          const existingIds = new Set(prev.map(i => i.id));
          const uniqueNew = newItems.filter(i => !existingIds.has(i.id));

          if (uniqueNew.length === 0) {
            setHasMore(false);
          }

          return [...prev, ...uniqueNew];
        });
      } else {
        if (isInitial) {
          // If API returns empty, maybe keep cache or clear? 
          // Let's keep cache if we have error, but if success empty, clear.
        }
        setHasMore(false);
      }

    } catch (error) {
      console.error('Feed error:', error);
      // Keep showing cached if error
      if (feedItems.length === 0) setError(error.message || "Failed to load feed");
    } finally {
      setLoading(false);
    }
  };

  const loadMoreFeed = () => {
    loadFeed(false);
  };

  const toggleReveal = useCallback((id) => {
    setRevealedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  }, []);

  const handleSmartWordClick = useCallback((word, fullText, translation, feedId) => {
    console.log("Word clicked:", word);
    // Simple log for now, or trigger MeaningPopup too? User didn't specify.
  }, []);

  // Case 1: Just Open Popup (No Fetch)
  const handleMeaningClick = useCallback((source, target) => {
    setPopup({ source, target });
    setWordInfo(null);
    setLoadingWord(false);
  }, []);

  // Case 2: Fetch Word Meaning (Triggered by click INSIDE popup)
  const handleWordLookup = useCallback(async (word) => {
    if (!popup) return;
    setLoadingWord(true);
    setWordInfo(null);
    const userId = JSON.parse(localStorage.getItem('user'))?.id;

    try {
      const gptRes = await apiService.wordMeaning({
        source: popup.source,
        target: popup.target,
        word: word,
        userId
      });

      const meanings = gptRes.data;
      let wordId = gptRes.wordId;
      let isFavorite = gptRes.isFavorite || false;

      // 1. If we got ID from DB, use it
      if (wordId) {
        setWordInfo({ word, meanings, id: wordId, isFavorite });
      }
      // 2. If new word (GPT), save it and get new ID
      else if (userId && meanings && meanings.length > 0) {
        setWordInfo({ word, meanings }); // Show content first

        const primaryMeaning = meanings[0];
        const translation = primaryMeaning.의미1 || primaryMeaning.meaning || primaryMeaning.definition || '';
        const exampleSentence = primaryMeaning.예문1 || primaryMeaning.example || primaryMeaning.exampleSentence || '';

        apiService.saveWordHistory(userId, {
          word,
          translation,
          exampleSentence,
          meanings
        }).then(saveRes => {
          if (saveRes.success && saveRes.data && saveRes.data.id) {
            // Update state with new ID so Favorite button appears
            setWordInfo(prev => ({ ...prev, id: saveRes.data.id }));
          }
        }).catch(e => console.error("Failed to save word history:", e));
      } else {
        // No ID, just show
        setWordInfo({ word, meanings });
      }

    } catch (e) {
      console.error(e);
      setWordInfo({ word: word, meanings: [] });
    } finally {
      setLoadingWord(false);
    }
  }, [popup]);

  const handleWordHistory = async (word, meanings) => {
    // Re-implementation of SentenceExtractor's word history save
    // For brevity, skipping unless specifically requested or I reuse the logic.
    // MeaningPopup handles some of this? No, MeaningPopup is UI.
    // Actually SentenceExtractor handles the save logic.
    // I'll keep it simple: Just show meanings for now.
  }

  const handleLike = useCallback((item) => {
    setFeedItems(prev => prev.map(fi =>
      fi.id === item.id ? { ...fi, likes: (fi.likes || 0) + 1, liked: true } : fi
    ));
    apiService.likeFeedItem(item.id);
  }, []);

  const handleShare = async (item) => {
    const shareData = {
      title: 'Nureum Voca',
      text: `${item.text}\n\n${item.translation}`,
      url: window.location.href
    };
    if (navigator.share) {
      await navigator.share(shareData).catch(() => { });
    } else {
      await navigator.clipboard.writeText(shareData.text);
      alert('Copied to clipboard!');
    }
  };

  const getSourceDisplay = (item) => item.sourceCode || "News";
  const getTimeDisplay = () => "Just now";

  return (
    <FeedContainer>
      <HeaderBar>
        <HeaderTitle>Nureum Feed</HeaderTitle>
        <SettingsButton onClick={() => navigate('/feed-settings')}>
          <Settings size={22} />
        </SettingsButton>
      </HeaderBar>

      <FeedList>
        {feedItems.map((item, index) => {
          const user = JSON.parse(localStorage.getItem('user'));
          return (
            <FeedItem
              key={item._renderId}
              item={item}
              revealedIds={revealedIds}
              toggleReveal={toggleReveal}
              handleLike={handleLike}
              handleShare={handleShare}
              setAnalysisText={setAnalysisText}
              setAnalysisFeedId={setAnalysisFeedId}
              setShowAnalysis={setShowAnalysis}
              setDeepDiveItem={setDeepDiveItem}
              getSourceDisplay={getSourceDisplay}
              getTimeDisplay={getTimeDisplay}
              handleSmartWordClick={handleSmartWordClick}
              onMeaningClick={handleMeaningClick} // Pass handler
              hasAnalysis={!!analyzedStatus[item.id]}
              isGuest={false} // Allow DB interactions for logged-in Guest
              isGuestUser={user?.isGuest} // Trigger animation
              isFirst={index === 0}
            />
          );
        })}
      </FeedList>

      {!loading && error && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#ff4b4b' }}>
          <p>Error: {error}</p>
          <button onClick={() => loadFeed(true)} style={{ marginTop: '10px', padding: '8px 16px', background: '#333', color: '#fff', border: 'none', borderRadius: '4px' }}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && feedItems.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888' }}>
          <p>No news available yet.</p>
          <p style={{ fontSize: '14px', marginTop: '10px' }}>
            The news collector is running. Please check back in a few minutes.
          </p>
        </div>
      )}

      {/* Sentinel for Infinite Scroll */}
      <div ref={lastItemRef} style={{ height: '20px', background: 'transparent' }} />

      {loading && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>Loading more...</div>
      )}

      {deepDiveItem && (
        <DeepDiveView
          item={deepDiveItem}
          onClose={() => setDeepDiveItem(null)}
          onWordClick={handleSmartWordClick}
        />
      )}

      {/* Modal Fallback */}
      {showAnalysis && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: '#141414', zIndex: 2000, overflowY: 'auto'
        }}>
          <div style={{ padding: '60px 20px 20px' }}>
            <button
              onClick={() => setShowAnalysis(false)}
              style={{ position: 'fixed', top: 16, left: 16, background: 'none', border: 'none', color: 'white', fontSize: 16 }}
            >
              <ArrowLeft /> Back
            </button>
            <SentenceExtractor
              isModal={true}
              initialText={analysisText}
              feedId={analysisFeedId}
              autoAnalyze={true}
              onAnalysisComplete={(result) => {
                // result: { feedId, data, docId }
                console.log("Analysis Completed via Modal", result);

                // 1. Update Analyzed Status to trigger auto-expand
                if (result.feedId) {
                  setAnalyzedStatus(prev => ({
                    ...prev,
                    [result.feedId]: result.data // Save the DATA directly for instant render
                  }));
                }

                // 2. Optionally close modal? Or let user close.
                // User typically wants to see it. 
                // But since we are auto-expanding the feed item behind, maybe we should close?
                // The user said "Analyze를 클릭하면... 분석 결과 를 열어줘"
                // If we close the modal, the feed item should be expanded.
                // Let's close modal so they see the inline result.
                setShowAnalysis(false);
              }}
            />
          </div>
        </div>
      )}

      {/* Meaning Popup */}
      {popup && (
        <MeaningPopup
          source={popup.source}
          target={popup.target}
          onClose={() => { setPopup(null); setWordInfo(null); setLoadingWord(false); }}
          wordInfo={wordInfo}
          onWordClick={handleWordLookup} // Trigger fetch on click
          loadingWord={loadingWord}
          onUpdateWordInfo={setWordInfo}
        />
      )}

    </FeedContainer>
  );
}

/* --- Deep Dive View Component --- */
const DeepDiveView = ({ item, onClose, onWordClick }) => {
  const paragraphs = item.fullText ? item.fullText.split('\n').filter(p => p.trim()) : [item.text];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      background: '#000', zIndex: 2000, overflowY: 'auto'
    }}>
      <div style={{
        position: 'sticky', top: 0, background: 'rgba(0,0,0,0.9)', borderBottom: '1px solid #333',
        padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backdropFilter: 'blur(10px)'
      }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
          <ArrowLeft size={24} /> Back
        </button>
        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Full Article</div>
        <div style={{ width: 24 }}></div>
      </div>

      <div style={{ padding: '24px', maxWidth: '680px', margin: '0 auto', color: '#e0e0e0', lineHeight: 1.8, fontSize: '18px' }}>
        <h1 style={{ fontSize: '22px', marginBottom: '16px', color: '#fff' }}>{item.title || "News Article"}</h1>
        {item.imageUrl && <img src={item.imageUrl} alt="Article" style={{ width: '100%', borderRadius: '12px', marginBottom: '24px' }} />}

        {paragraphs.map((para, idx) => (
          <div key={idx} style={{ marginBottom: '24px' }}>
            <KaraokeText text={para} onWordClick={(word) => onWordClick(word, para, "", item.id)} />
          </div>
        ))}

        <div style={{ marginTop: 40, padding: 20, background: '#111', borderRadius: 8, fontSize: '14px', color: '#888' }}>
          <div>Source: {item.sourceCode || 'News'}</div>
          {item.originalLink && (
            <a href={item.originalLink} target="_blank" rel="noreferrer" style={{ color: '#39ace7', display: 'block', marginTop: 8 }}>View Original Source</a>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedPage;
